import { useEffect, useRef, useState } from 'react';
import { useOrg } from '../app/OrgProvider';
import { AppLink } from '../components/Nav';
import { CheckIcon, DownloadIcon, TrashIcon } from '../components/icons';
import { Badge, Banner, Button, CopyButton, Input, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { shareUrl } from '../lib/env';
import { bytes } from '../lib/format';
import { updateVideo } from '../queries/videos';
import { extensionFor } from './codec';
import { TakePreview } from './TakePreview';
import type { RecorderApi } from './useRecorder';

/**
 * The screen after "Finish".
 *
 * The share link is already live at this point: the video row and its share
 * id were created the moment recording started, and /complete has handed the
 * file to the transcode pipeline. So the copy-link button works immediately;
 * anyone who opens it before the HLS ladder is ready sees a "still processing"
 * player rather than a broken one. Title edits are a PATCH after the fact,
 * which is why nothing here blocks the pipeline.
 */

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function RecorderPreview({
  recorder,
  defaultTitle,
  onDone,
  onRecordAnother,
}: {
  recorder: RecorderApi;
  defaultTitle: string;
  onDone?: () => void;
  onRecordAnother?: () => void;
}) {
  const videoId = recorder.result?.id ?? recorder.upload.videoId;
  const shareId = recorder.shareId;
  const { org } = useOrg();
  const [title, setTitle] = useState(recorder.result?.title ?? defaultTitle);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const lastSavedRef = useRef(title);

  useEffect(() => {
    if (saveState !== 'saved') return;
    const timer = window.setTimeout(() => setSaveState('idle'), 2000);
    return () => window.clearTimeout(timer);
  }, [saveState]);

  const uploadFailed = recorder.upload.phase === 'failed' || Boolean(recorder.upload.error);
  const completed = recorder.result !== null;

  const saveTitle = async () => {
    const next = title.trim();
    if (!videoId || !next || next === lastSavedRef.current) return;
    setSaveState('saving');
    setSaveError(null);
    try {
      await updateVideo(videoId, { title: next });
      lastSavedRef.current = next;
      setSaveState('saved');
      // The title itself never leaves the browser; only that it changed.
      if (shareId) track('Video Renamed', { video_id: shareId });
    } catch (error) {
      setSaveState('error');
      setSaveError(errorMessage(error));
    }
  };

  const discard = async () => {
    setDiscarding(true);
    try {
      await recorder.discard();
      onDone?.();
    } finally {
      setDiscarding(false);
    }
  };

  const retry = async () => {
    setRetrying(true);
    try {
      await recorder.retryUpload();
    } finally {
      setRetrying(false);
    }
  };

  // On the team's own domain where one is serving; the Slate address is
  // always valid too, so an older payload simply gives the old link.
  const link = shareId ? shareUrl(shareId, org?.linkDomain) : null;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="space-y-4">
        <TakePreview recorder={recorder} />

        <Input
          label="Title"
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => void saveTitle()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          error={saveError}
          hint="Saved when you click away."
        />

        <div className="flex h-4 items-center gap-1.5 text-caption text-chalk-faint">
          {saveState === 'saving' ? (
            <>
              <Spinner size={12} /> Saving…
            </>
          ) : null}
          {saveState === 'saved' ? (
            <>
              <CheckIcon size={12} className="text-go" /> Saved
            </>
          ) : null}
        </div>
      </div>

      <aside className="space-y-4">
        {uploadFailed ? (
          <Banner
            tone="danger"
            title="Some of this recording did not upload"
            action={
              <Button variant="secondary" onClick={() => void retry()} busy={retrying}>
                Retry upload
              </Button>
            }
          >
            {recorder.upload.error ?? 'The connection dropped part-way through.'} Your take is
            still here. Download it below if you would rather keep the file.
          </Banner>
        ) : null}

        {completed ? (
          <div className="card space-y-3 p-4">
            <div className="flex items-center justify-between">
              <span className="text-caption font-medium text-chalk-dim">Share link</span>
              <Badge tone="caution" pulse>
                Processing
              </Badge>
            </div>
            <p className="break-all rounded bg-slate-900 px-2.5 py-2 font-mono text-caption text-chalk-dim">
              {link}
            </p>
            <p className="text-caption text-chalk-faint">
              The link works right now. Whoever opens it sees a short "still processing" screen
              until the transcode finishes, usually within a minute or two of the recording's
              length.
            </p>
            {link && shareId ? (
              <CopyButton
                value={link}
                variant="primary"
                className="w-full"
                onCopied={() => track('Share Link Copied', { video_id: shareId, surface: 'recorder' })}
              />
            ) : null}
          </div>
        ) : (
          <div className="card space-y-2 p-4">
            <p className="text-body text-chalk">Not shareable yet</p>
            <p className="text-caption text-chalk-faint">
              The upload has not finished, so there is no link to hand out. Retry it above, or
              download the file and upload it later.
            </p>
          </div>
        )}

        <div className="card space-y-3 p-4">
          <p className="text-caption font-medium text-chalk-dim">This take</p>
          <dl className="space-y-1.5 text-caption">
            <div className="flex justify-between">
              <dt className="text-chalk-faint">Length</dt>
              <dd className="tnum text-chalk-dim" data-timecode>
                {Math.round(recorder.elapsedMs / 1000)}s
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-chalk-faint">Size</dt>
              <dd className="tnum text-chalk-dim">{bytes(recorder.upload.capturedBytes)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-chalk-faint">Parts uploaded</dt>
              <dd className="tnum text-chalk-dim">{recorder.upload.partsUploaded}</dd>
            </div>
          </dl>
        </div>

        <div className="flex flex-col gap-2">
          {shareId && completed ? (
            <AppLink to="/v/$shareId" params={{ shareId }}>
              <Button variant="secondary" className="w-full">
                Open the player
              </Button>
            </AppLink>
          ) : null}

          {onRecordAnother ? (
            <Button variant="secondary" onClick={onRecordAnother}>
              Record another
            </Button>
          ) : null}

          {recorder.previewBlob ? (
            <a
              href={recorder.previewUrl ?? '#'}
              download={`${title.replace(/[^\w\s-]/g, '').trim() || 'slate-recording'}.${
                recorder.codec ? extensionFor(recorder.codec.contentType) : 'webm'
              }`}
              className={cx(
                'inline-flex h-9 items-center justify-center gap-2 rounded border border-hairline',
                'bg-slate-800 px-3.5 text-body text-chalk transition-colors duration-120 hover:bg-slate-700',
              )}
            >
              <DownloadIcon size={15} />
              Download the file
            </a>
          ) : null}

          <Button variant="danger" onClick={() => void discard()} busy={discarding}>
            <TrashIcon size={15} />
            Discard this recording
          </Button>
        </div>
      </aside>
    </div>
  );
}
